// ─── Form Service ────────────────────────────────────────────────────────────
// Uses Web3Forms (https://web3forms.com) — free, CORS-safe, works from any
// origin including localhost.  No domain whitelisting required.
//
// HOW TO GET YOUR ACCESS KEY:
//   1. Go to https://web3forms.com
//   2. Enter your email (info@optibiz.co.ke) and click "Create Access Key"
//   3. Copy the key you receive and paste it below (or set VITE_WEB3FORMS_KEY in .env)
// ─────────────────────────────────────────────────────────────────────────────

const WEB3FORMS_ACCESS_KEY =
  import.meta.env.VITE_WEB3FORMS_KEY || "9f5fdd6d-04ed-44e9-b3f7-24e2aff73dfa";

const ADMIN_EMAIL =
  import.meta.env.VITE_ADMIN_EMAIL || "optibizsuite@gmail.com";

// Public, email-safe (PNG — email clients don't render SVG) logo on a light band.
const LOGO_URL = "https://optibizsuite.co.ke/optibiz-logo-email.png";

export interface SubscriptionData {
  email: string;
  name?: string;
  subject?: string;
  message?: string;
  company?: string;
  phone?: string;
}

// ─── Branded HTML email template ─────────────────────────────────────────────
// Table-based layout (the only reliable technique across Gmail/Outlook/Apple
// Mail). Web3Forms substitutes {{variable}} placeholders from the payload.
function buildEmailTemplate(): string {
  const brandBlue = "#004183";
  const brandOrange = "#F67535";
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0;padding:0;background-color:#eef2f7;font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#eef2f7;padding:32px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,65,131,0.08);">

          <!-- Logo band (light, so the navy wordmark reads) -->
          <tr>
            <td align="center" style="background-color:#ffffff;padding:32px 24px 20px 24px;border-bottom:1px solid #eef2f7;">
              <img src="${LOGO_URL}" alt="OptiBiz Suite" width="200" style="display:block;width:200px;max-width:70%;height:auto;">
            </td>
          </tr>

          <!-- Accent bar (solid fallback + gradient for clients that support it) -->
          <tr><td style="height:5px;background-color:${brandBlue};background:linear-gradient(90deg,${brandBlue},#3FA26C,${brandOrange});font-size:0;line-height:0;">&nbsp;</td></tr>

          <!-- Heading -->
          <tr>
            <td style="padding:32px 40px 8px 40px;">
              <p style="margin:0 0 6px 0;font-size:13px;letter-spacing:1px;text-transform:uppercase;color:${brandOrange};font-weight:700;">New Website Enquiry</p>
              <h1 style="margin:0;font-size:22px;line-height:1.3;color:${brandBlue};font-weight:700;">{{subject}}</h1>
              <p style="margin:14px 0 0 0;font-size:15px;line-height:1.6;color:#5b6b7c;">You've received a new submission from the OptiBiz Suite website. Details are below.</p>
            </td>
          </tr>

          <!-- Details card -->
          <tr>
            <td style="padding:20px 40px 8px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f7f9fc;border:1px solid #e6ecf3;border-radius:12px;">
                <tr>
                  <td style="padding:18px 22px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding:8px 0;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#8a99a8;font-weight:700;width:120px;vertical-align:top;">Name</td>
                        <td style="padding:8px 0;font-size:15px;color:#1f2d3d;font-weight:600;">{{name}}</td>
                      </tr>
                      <tr><td colspan="2" style="border-top:1px solid #e6ecf3;font-size:0;line-height:0;">&nbsp;</td></tr>
                      <tr>
                        <td style="padding:8px 0;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#8a99a8;font-weight:700;vertical-align:top;">Email</td>
                        <td style="padding:8px 0;font-size:15px;"><a href="mailto:{{email}}" style="color:${brandBlue};text-decoration:none;font-weight:600;">{{email}}</a></td>
                      </tr>
                      <tr><td colspan="2" style="border-top:1px solid #e6ecf3;font-size:0;line-height:0;">&nbsp;</td></tr>
                      <tr>
                        <td style="padding:8px 0;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#8a99a8;font-weight:700;vertical-align:top;">Company</td>
                        <td style="padding:8px 0;font-size:15px;color:#1f2d3d;">{{company}}</td>
                      </tr>
                      <tr><td colspan="2" style="border-top:1px solid #e6ecf3;font-size:0;line-height:0;">&nbsp;</td></tr>
                      <tr>
                        <td style="padding:8px 0;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#8a99a8;font-weight:700;vertical-align:top;">Phone</td>
                        <td style="padding:8px 0;font-size:15px;color:#1f2d3d;">{{phone}}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Message -->
          <tr>
            <td style="padding:16px 40px 8px 40px;">
              <p style="margin:0 0 8px 0;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#8a99a8;font-weight:700;">Message</p>
              <div style="background-color:#ffffff;border:1px solid #e6ecf3;border-left:4px solid ${brandOrange};border-radius:10px;padding:16px 18px;font-size:15px;line-height:1.65;color:#2c3e50;">{{message}}</div>
            </td>
          </tr>

          <!-- Reply CTA -->
          <tr>
            <td align="center" style="padding:24px 40px 8px 40px;">
              <a href="mailto:{{email}}" style="display:inline-block;background-color:${brandBlue};background:linear-gradient(90deg,${brandBlue},${brandOrange});color:#ffffff;text-decoration:none;font-size:15px;font-weight:700;padding:13px 32px;border-radius:999px;">Reply to {{name}}</a>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding:28px 40px 32px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-top:1px solid #eef2f7;">
                <tr>
                  <td style="padding-top:18px;text-align:center;font-size:12px;line-height:1.6;color:#9aa7b5;">
                    OptiBiz Suite &middot; Daima Towers, Eldoret, Uasin Gishu, Kenya<br>
                    <a href="https://optibizsuite.co.ke" style="color:${brandBlue};text-decoration:none;">optibizsuite.co.ke</a>
                    &nbsp;&middot;&nbsp;
                    <a href="mailto:info@optibiz.co.ke" style="color:${brandBlue};text-decoration:none;">info@optibiz.co.ke</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export const formService = {
  /**
   * Sends a subscription / demo-request email via Web3Forms.
   * Web3Forms forwards the submission to ADMIN_EMAIL using our branded template.
   */
  async submitDemoRequest(data: SubscriptionData): Promise<void> {
    const name = data.name || data.email.split("@")[0];
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,

      // Web3Forms routing — which inbox to deliver to
      from_name: "OptiBiz Suite Website",
      to_email: ADMIN_EMAIL,
      replyto: data.email,

      // Submission content (also used as {{variable}} substitutions in template)
      name,
      email: data.email,
      subject: data.subject || "New OptiBiz Suite Demo Request",
      message:
        data.message ||
        `A new user has requested a demo of OptiBiz Suite from the website.`,

      // Extra fields shown in the email body
      company: data.company || "Not specified",
      phone: data.phone || "Not provided",

      // Custom branded HTML email (overrides the default Web3Forms template)
      template: buildEmailTemplate(),

      // Bot-protection settings
      botcheck: false,
    };

    const response = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok || result.success === false) {
      console.error("Web3Forms error:", result);
      throw new Error(result.message || "Failed to send request. Please try again.");
    }
  },
};
